import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnalysisAPI, ExportsAPI, OdkAPI } from "../api/endpoints";
import { getToken } from "../api/client";
import ProjectFormPicker from "../components/ProjectFormPicker";
import { Card, PageHeader, Spinner, ErrorBanner, HelpBox, KindBadge, AccionesSeleccion } from "../components/Layout";
import { varKind } from "../lib/varkind";

export default function ReportsPage() {
  const [projectId, setProjectId] = useState<number | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [title, setTitle] = useState("Informe Analista");
  const [subtitle, setSubtitle] = useState("");
  const [narrative, setNarrative] = useState("");
  const [selectedVars, setSelectedVars] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busyPdf, setBusyPdf] = useState(false);
  const [busyIA, setBusyIA] = useState(false);
  const [notaIA, setNotaIA] = useState<string | null>(null);

  const colsQ = useQuery({
    queryKey: ["dict", projectId, formId],
    queryFn: () => OdkAPI.variables(projectId!, formId!),
    enabled: !!projectId && !!formId,
  });
  const cols = (colsQ.data ?? []).filter((v) => !v.is_technical);

  // Descargar el diccionario es un permiso aparte: se pregunta antes de ofrecer el
  // botón. Un botón que se ve, se pulsa y devuelve 403 es peor que uno apagado que
  // explica por qué.
  const accesoQ = useQuery({
    queryKey: ["acceso", projectId, formId],
    queryFn: () => OdkAPI.acceso(projectId!, formId!),
    enabled: !!projectId && !!formId,
  });
  const puedeDescargarDiccionario = accesoQ.data?.puede_descargar_diccionario ?? true;

  const toggleVar = (name: string) =>
    setSelectedVars((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));

  const download = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // Descarga autenticada. El motivo que da el servidor se enseña tal cual: «Error
  // al exportar» no le dice a nadie que le falta un permiso.
  const descargar = async (url: string, nombre: string) => {
    setErr(null);
    const token = getToken();
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      let motivo = `El servidor respondió ${res.status}.`;
      try {
        const cuerpo = await res.json();
        if (cuerpo?.detail) motivo = String(cuerpo.detail);
      } catch { /* la respuesta no traía JSON: queda el código de estado */ }
      throw new Error(motivo);
    }
    download(await res.blob(), nombre);
  };

  const exportData = async () => {
    if (!projectId || !formId) return;
    try {
      await descargar(ExportsAPI.dataExcelUrl(projectId, formId), `${formId}.xlsx`);
    } catch (e) { setErr(e instanceof Error ? e.message : "Error"); }
  };

  const exportDict = async () => {
    if (!projectId || !formId) return;
    try {
      await descargar(ExportsAPI.dictionaryExcelUrl(projectId, formId), `diccionario_${formId}.xlsx`);
    } catch (e) { setErr(e instanceof Error ? e.message : "Error"); }
  };

  // Redacta la conclusión con el modelo configurado en el panel, a partir del
  // informe descriptivo de las variables elegidas.
  const redactarConIA = async () => {
    if (!projectId || !formId || selectedVars.length === 0) return;
    setBusyIA(true);
    setErr(null);
    setNotaIA(null);
    try {
      const resumen = await AnalysisAPI.descriptiveReport({
        project_id: projectId,
        form_id: formId,
        variables: selectedVars,
      });
      const r = await AnalysisAPI.ai({
        project_id: projectId,
        form_id: formId,
        data_summary: resumen,
      });
      setNarrative(r.analysis);
      setNotaIA(
        `Redactado por ${r.modelo} · ${r.tokens_entrada} tokens de entrada, ` +
        `${r.tokens_salida} de salida` + (r.costo ? ` · $${r.costo.toFixed(4)}` : "")
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "No se pudo redactar el informe con IA.");
    } finally {
      setBusyIA(false);
    }
  };

  const exportPdf = async () => {
    if (!projectId || !formId || selectedVars.length === 0) return;
    setBusyPdf(true); setErr(null);
    try {
      const blob = await ExportsAPI.descriptivePdf({
        project_id: projectId, form_id: formId, variables: selectedVars,
        title, subtitle: subtitle || undefined, narrative: narrative || undefined,
      });
      download(blob, `informe_${formId}.pdf`);
    } catch (e) { setErr(e instanceof Error ? e.message : "Error"); }
    finally { setBusyPdf(false); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <PageHeader title="Informes y exportación" subtitle="Datos en Excel, diccionario e informe PDF con las variables que elijas." />

      <HelpBox title="Guía: qué genera cada exportación">
        <p><b>Excel (datos).</b> Todas las respuestas del formulario tal como están en ODK (etiquetadas), una fila por envío.</p>
        <p><b>Excel (diccionario).</b> El listado de variables: nombre técnico, pregunta, tipo y opciones de respuesta.</p>
        <p>
          <b>Informe PDF.</b> Un informe descriptivo con una sección por cada variable que marques:
          las <b>categóricas</b> salen con tabla de frecuencias (n, % y margen de error al 95%);
          las <b>numéricas</b> con media, desviación, mínimo, cuartiles, mediana y máximo.
          Usa <b>Todas / Ninguna</b> para marcar rápido y añade una conclusión (puedes pegar el análisis IA);
          admite <b>**negritas**</b> y saltos de línea.
        </p>
      </HelpBox>

      <Card className="mb-4">
        <ProjectFormPicker projectId={projectId} formId={formId}
          onProject={(id) => { setProjectId(id); setFormId(null); setSelectedVars([]); }}
          onForm={(f) => { setFormId(f); setSelectedVars([]); }} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <div className="font-medium text-marca mb-2">Exportar datos</div>
          <div className="flex flex-col gap-2">
            <button onClick={exportData} disabled={!projectId || !formId}
              className="btn-sec">Descargar Excel (datos)</button>
            <button onClick={exportDict} disabled={!projectId || !formId || !puedeDescargarDiccionario}
              className="btn-sec"
              title={puedeDescargarDiccionario ? undefined : "No tienes permiso para descargar el diccionario de esta encuesta."}
            >Descargar Excel (diccionario)</button>
            {formId && !puedeDescargarDiccionario && (
              <p className="text-xs text-estado-serio">
                El administrador no te ha dado permiso para descargar el diccionario de esta
                encuesta. Puedes consultarlo en la pantalla <b>Diccionario</b>.
              </p>
            )}
          </div>
        </Card>

        <Card>
          <div className="font-medium text-marca mb-2">Informe PDF</div>
          <label className="etiqueta">Título</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="input mb-2" />
          <label className="etiqueta">Subtítulo</label>
          <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className="input mb-2" />

          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <label className="etiqueta mb-0">Variables a incluir</label>
            <AccionesSeleccion
              visibles={cols.map((c) => c.name)}
              seleccionados={selectedVars}
              onCambiar={setSelectedVars}
              nombre="variable(s)"
            />
          </div>
          <div className="max-h-48 overflow-y-auto bg-white border border-grid rounded-md p-2 mb-2">
            {cols.length === 0 && (
              <div className="text-xs text-tinta-3 p-1">Elige proyecto y formulario para ver las variables.</div>
            )}
            {cols.map((c) => (
              <label key={c.name} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                <input type="checkbox" checked={selectedVars.includes(c.name)} onChange={() => toggleVar(c.name)} className="accent-marca" />
                <span>{c.label || c.name}</span>
                <KindBadge kind={varKind(c)} />
              </label>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <label className="etiqueta mb-0">Conclusión</label>
            <button
              onClick={redactarConIA}
              disabled={busyIA || !projectId || !formId || selectedVars.length === 0}
              className="btn-sec text-xs"
              title="Usa el modelo configurado en Configuración → Inteligencia artificial"
            >
              {busyIA ? "Redactando…" : "✨ Redactar con IA"}
            </button>
          </div>
          <textarea value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={6}
            className="input mb-1"
            placeholder="Escríbela, pégala, o pulsa «Redactar con IA» para que la escriba el modelo sobre las variables marcadas." />
          {notaIA && <p className="text-xs text-tinta-3 mb-2">{notaIA}</p>}
          {busyIA && <div className="mb-2"><Spinner /></div>}
          <button onClick={exportPdf} disabled={busyPdf || !projectId || !formId || selectedVars.length === 0}
            className="btn">
            {busyPdf ? <Spinner /> : "Generar PDF"}
          </button>
          {(!formId || selectedVars.length === 0) && (
            <span className="ml-3 text-xs text-estado-serio">
              {!formId ? "Elige un formulario." : "Marca al menos una variable."}
            </span>
          )}
        </Card>
      </div>
      {err && <div className="mt-4"><ErrorBanner message={err} /></div>}
    </div>
  );
}
